/**
 * The world bosses' gear (docs/systems/WORLD_BOSSES.md chests, docs/systems/ITEMS_V2.md §1.6–1.7, R5): each boss's 4-piece set
 * (data/itemSets.ts), the two pieces its chest names besides (a manual, a treasure) and the six unique pieces
 * (data/uniques.ts). The chests pay them rolled at the boss's level (systems/itemCraft.ts). Stats only on the armour:
 * the character models are final (`visual: ''`); weapons reuse the blade tints. Data only; `items.ts` lists them.
 */
import type { ItemDef } from './items';

/** One set piece: epic, its slot's main stat sized like the ladder's epics. */
const piece = (
  id: string,
  name: string,
  set: string,
  slot: 'head' | 'body' | 'legs' | 'feet',
  desc: string,
): ItemDef => ({
  id,
  name,
  slot,
  rarity: 'epic',
  set,
  ilv: 22,
  desc,
  stats:
    slot === 'body'
      ? { def: 10, maxHp: 45 }
      : slot === 'head'
        ? { magicDef: 6, maxHp: 35 }
        : slot === 'legs'
          ? { def: 6, maxStamina: 20 }
          : { dodge: 5, speed: 0.03 },
  visual: '',
  worth: 900,
});

const token = (id: string, name: string, rarity: 'epic' | 'legend', desc: string): ItemDef => ({
  id,
  name,
  slot: 'treasure',
  rarity,
  ilv: 22,
  desc,
  stats: {},
  visual: '',
  worth: rarity === 'legend' ? 5000 : 2000,
});

export const BOSS_GEAR: readonly ItemDef[] = [
  // ── ชุดวานรทงปี้ (ลิงยักษ์ทงปี้) ──
  piece('wb-bell-sash', 'ผ้าคาดเอวกระดิ่งวานร', 'wb-ape', 'body', 'ผ้าคาดเอวผูกกระดิ่งทองแดงของลิงยักษ์ ดังกังวานทุกครั้งที่เหาะข้ามยอดไม้'),
  piece('wb-ape-band', 'ผ้ารัดเกล้าขนเงิน', 'wb-ape', 'head', 'ผ้ารัดเกล้าถักขนสีเงินของลิงยักษ์ ทำให้ตาไวเหมือนวานรบนผา'),
  piece('wb-ape-greaves', 'สนับแข้งแขนยาว', 'wb-ape', 'legs', 'สนับแข้งหนังลิงยักษ์ เบาจนกระโดดข้ามกิ่งไม้ได้ไม่รู้เหนื่อย'),
  piece('wb-ape-boots', 'รองเท้าเท้าวานร', 'wb-ape', 'feet', 'รองเท้าพื้นบางเกาะกิ่งไม้ได้แน่น ขึ้นผาชันได้คล่องแคล่ว'),
  // ── ชุดขนนกเพลิง (นกเพลิงปี้ฟาง) ──
  piece('wb-firebird-cape', 'ผ้าคลุมขนนกเพลิง', 'wb-firebird', 'body', 'ผ้าคลุมเย็บจากขนนกเพลิงที่ไม่มีวันมอด อุ่นแม้กลางหิมะ'),
  piece('wb-firebird-crown', 'มงกุฎหงอนเพลิง', 'wb-firebird', 'head', 'มงกุฎทองประดับหงอนนกเพลิง เปลวไฟเต้นอยู่ปลายขนทุกเส้น'),
  piece('wb-firebird-greaves', 'สนับแข้งเกล็ดถ่าน', 'wb-firebird', 'legs', 'สนับแข้งเกล็ดสีถ่านที่ยังคุกรุ่น ไฟไม่อาจลามขึ้นขา'),
  piece('wb-firebird-boots', 'รองเท้าเถ้านิรันดร์', 'wb-firebird', 'feet', 'รองเท้าที่หล่อจากเถ้าของนกเพลิง เดินบนถ่านแดงได้ไม่ไหม้'),
  // ── ชุดกระดองอสนี (เต่าคะนองเหล่ยกุย) ──
  piece('wb-shell-pauldron', 'เกราะไหล่กระดองอสนี', 'wb-thunder-turtle', 'body', 'เกราะไหล่จากกระดองเต่าคะนอง สายฟ้าแล่นตามลายกระดองเมื่อรับดาบ'),
  piece('wb-shell-helm', 'หมวกกระดองฟ้าคำราม', 'wb-thunder-turtle', 'head', 'หมวกครอบกระดองหนา ได้ยินเสียงฟ้าคำรามแผ่วๆ อยู่ข้างใน'),
  piece('wb-shell-greaves', 'สนับแข้งเกล็ดอสนี', 'wb-thunder-turtle', 'legs', 'สนับแข้งเกล็ดเต่าซ้อนกันแน่น ยืนรับแรงกระแทกได้ไม่ถอย'),
  piece('wb-shell-boots', 'รองเท้าตีนเต่าหิน', 'wb-thunder-turtle', 'feet', 'รองเท้าพื้นหินหนัก ตั้งหลักแน่นดุจเต่ายักษ์ปักเท้าลงทราย'),
  // ── the chests' other pieces ──
  {
    id: 'manual-wb-long-arm',
    name: 'ตำราฝ่ามือแขนยาวทงปี้',
    slot: 'manual',
    rarity: 'legend',
    ilv: 22,
    desc: 'ตำราที่บันทึกท่วงท่าของลิงยักษ์ทงปี้ ฝ่ามือยื่นได้ไกลกว่าที่ตาเห็น',
    stats: {},
    art: { id: 'cloud-palm', pct: 0.15 },
    visual: '',
    worth: 1200,
  },
  {
    id: 'treasure-wb-calm-thunder-stone',
    name: 'ศิลาสงบอสนี',
    slot: 'treasure',
    rarity: 'legend',
    ilv: 24,
    desc: 'ศิลาจากท้องเต่าคะนอง กำไว้แล้วใจนิ่งดุจท้องฟ้าหลังพายุ',
    stats: { toughness: 10, guardHp: 0.05 },
    visual: '',
    worth: 1200,
  },
  // ── uniques (data/uniques.ts) ──
  {
    id: 'u-shadow-sword',
    name: 'กระบี่ซ่อนเงา',
    slot: 'weapon',
    kind: 'jian',
    rarity: 'legend',
    unique: 'u-shadow-sword',
    ilv: 24,
    desc: 'กระบี่ที่เงาของมันฟันได้เหมือนตัวจริง ยามปล่อยกระบี่บินเงาก็บินตามไปด้วย',
    stats: { atk: 22, crit: 0.03 },
    visual: 'azure',
    worth: 1500,
  },
  {
    id: 'u-moon-sabre',
    name: 'ดาบโลหิตกลืนจันทร์',
    slot: 'weapon',
    kind: 'dao',
    rarity: 'legend',
    unique: 'u-moon-sabre',
    ilv: 24,
    desc: 'ดาบที่ดื่มเลือดจนสีใบเหมือนจันทร์สีเลือด แผลที่มันเปิดไว้ มันกลับมาเก็บเอง',
    stats: { atk: 23, lifesteal: 0.02 },
    visual: 'crimson',
    worth: 1500,
  },
  {
    id: 'u-wind-fan',
    name: 'พัดสายลมนิรนาม',
    slot: 'weapon',
    kind: 'fan',
    rarity: 'legend',
    unique: 'u-wind-fan',
    ilv: 24,
    desc: 'พัดที่ไม่มีใครรู้ว่าใครวาด โบกครั้งเดียวลมพัดเอาความหม่นหมองออกไปหมด',
    stats: { atk: 20, magic: 6 },
    visual: 'azure',
    worth: 1500,
  },
  {
    id: 'u-mountain-staff',
    name: 'กระบองทลายภูผา',
    slot: 'weapon',
    kind: 'staff',
    rarity: 'legend',
    unique: 'u-mountain-staff',
    ilv: 24,
    desc: 'กระบองเหล็กดำหนักเท่าหินผา ทุบลงพื้นทีเดียวคนรอบข้างยืนไม่อยู่',
    stats: { atk: 21, def: 4 },
    visual: 'steel',
    worth: 1500,
  },
  {
    id: 'u-plum-needles',
    name: 'เข็มเงาเหมยแดง',
    slot: 'hidden',
    rarity: 'legend',
    unique: 'u-plum-needles',
    ilv: 24,
    desc: 'เข็มเงินเคลือบพิษดอกเหมยแดง ปักทีเดียวลมปราณของศัตรูติดขัด',
    stats: { atk: 6, crit: 0.03 },
    throwSkill: 'hidden-needles',
    visual: '',
    worth: 1500,
  },
  {
    id: 'u-cloud-boots',
    name: 'รองเท้าเมฆาเหิน',
    slot: 'feet',
    rarity: 'legend',
    unique: 'u-cloud-boots',
    ilv: 24,
    desc: 'รองเท้าที่เหยียบเมฆได้จริง ผู้สวมพุ่งกลางอากาศได้อีกหนึ่งครั้ง',
    stats: { dodge: 6, speed: 0.04 },
    visual: '',
    worth: 1500,
  },
  // ── the chests' pet and mount rolls ──
  // A chest pays these as items (systems/worldBosses.ts pays ids that exist). They are tokens: no stats, tradeable
  // (the market sells them); turning one into the pet or mount needs the owner of data/pets.ts and data/mounts.ts,
  // whose defs for these ids do not exist yet (docs/handoff/newdir/ITEMS-V2.md).
  token('pet-wb-ape-cub', 'ลูกวานรทงปี้ (ใบรับตัว)', 'epic', 'ใบรับตัวลูกวานรทงปี้จากหีบบอสโลก เมื่อมีคอกสัตว์เลี้ยงของบอสแล้วจะแลกเป็นตัวจริงได้'),
  token('pet-wb-firebird-chick', 'ลูกนกเพลิง (ใบรับตัว)', 'epic', 'ใบรับตัวลูกนกเพลิงจากหีบบอสโลก เมื่อมีคอกสัตว์เลี้ยงของบอสแล้วจะแลกเป็นตัวจริงได้'),
  token('pet-wb-turtle-hatchling', 'ลูกเต่าคะนอง (ใบรับตัว)', 'epic', 'ใบรับตัวลูกเต่าคะนองจากหีบบอสโลก เมื่อมีคอกสัตว์เลี้ยงของบอสแล้วจะแลกเป็นตัวจริงได้'),
  token('mount-wb-firebird', 'นกเพลิงปี้ฟาง (ใบรับตัว)', 'legend', 'ใบรับตัวพาหนะนกเพลิงปี้ฟางจากหีบบอสโลก เมื่อมีคอกพาหนะของบอสแล้วจะแลกเป็นตัวจริงได้'),
];

/** What each chest token turns into when redeemed (systems/itemCraft.ts `redeemToken`). */
export const BOSS_TOKEN_TARGET: Readonly<Record<string, { readonly kind: 'pet' | 'mount'; readonly id: string }>> = {
  'pet-wb-ape-cub': { kind: 'pet', id: 'ape-cub' },
  'pet-wb-firebird-chick': { kind: 'pet', id: 'firebird-chick' },
  'pet-wb-turtle-hatchling': { kind: 'pet', id: 'turtle-hatchling' },
  'mount-wb-firebird': { kind: 'mount', id: 'firebird' },
};
